import type { MaybeRefOrGetter } from "vue";
import { participationV1Api } from "@/api/participationV1.api";
import { StreamingSessionError } from "@/types/participationV1";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;

export type StreamingStatus = 'loading' | 'ready' | 'error';

// Locale key under app.experience.panel.errors
export type StreamingErrorKind = 'generic' | 'NO_CAPACITY' | StreamingSessionError;

// Errors for which a new attempt cannot succeed
const FINAL_ERRORS: StreamingErrorKind[] = [
  StreamingSessionError.PARTICIPATION_COMPLETED,
  StreamingSessionError.SITUATION_COMPLETED,
  StreamingSessionError.SITUATION_NOT_IN_PARTICIPATION,
  StreamingSessionError.SITUATION_NOT_CONFIGURED,
];

const KNOWN_REASONS = Object.values(StreamingSessionError) as string[];

export interface ConversationCallbacks {
  onEnded?: () => void;
  onError?: (message: string) => void;
}

// Requests the pixel streaming session of a situation from the message hub,
// polling while the hub answers "no_response_yet".
// Once the session is ready, long-polls the conversation state until it ends.
export function useStreamingSession(
  participationId: MaybeRefOrGetter<string>,
  situationId: MaybeRefOrGetter<string>,
  callbacks: ConversationCallbacks = {},
) {
  const url = ref<string | null>(null);
  const status = ref<StreamingStatus>('loading');
  const errorKind = ref<StreamingErrorKind | null>(null);
  const canRetry = computed(() => !errorKind.value || !FINAL_ERRORS.includes(errorKind.value));

  // Incremented on every (re)start so that responses of a previous run are ignored
  let run = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let sessionSalt: string = Date.now().toString()

  function stop() {
    run++;
    if (timer) clearTimeout(timer);
    timer = null;
  }

  function fail(kind: StreamingErrorKind) {
    errorKind.value = kind;
    status.value = 'error';
  }

  // The hub holds each request ~30s while the conversation is running
  async function watchConversation(currentRun: number) {
    try {
      const data = await participationV1Api.getConversationStatus(toValue(participationId), toValue(situationId), toValue(sessionSalt));
      if (currentRun !== run) return;

      switch (data.type) {
        case 'sidecar_conversation_ended':
          stop();
          callbacks.onEnded?.();
          return;
        case 'conversation_error':
          stop();
          callbacks.onError?.(data.message);
          return;
        default: // no_response_yet: the conversation is still running
          watchConversation(currentRun);
      }
    } catch (error) {
      if (currentRun !== run) return;
      // Transient failure (network, timeout, 5xx): keep watching without closing the conversation
      console.error("Error watching the conversation:", error);
      timer = setTimeout(() => watchConversation(currentRun), POLL_INTERVAL_MS);
    }
  }

  function start() {
    stop();
    const currentRun = run;
    const startedAt = Date.now();
    sessionSalt = startedAt.toString()
    url.value = null;
    errorKind.value = null;
    status.value = 'loading';

    const poll = async () => {
      try {
        const data = await participationV1Api.startSituation(toValue(participationId), toValue(situationId), toValue(sessionSalt));
        if (currentRun !== run) return;

        switch (data.type) {
          case 'no_response_yet':
            if (Date.now() - startedAt < POLL_TIMEOUT_MS) {
              timer = setTimeout(poll, POLL_INTERVAL_MS);
            } else {
              fail('generic');
            }
            return;
          case 'session_ready':
          case 'session_already_started':
            url.value = data.pixel_streaming_url;
            status.value = 'ready';
            watchConversation(currentRun);
            return;
          case 'no_capacity_for_conversation':
            fail('NO_CAPACITY');
            return;
          case 'conversation_launch_error':
            fail(KNOWN_REASONS.includes(data.reason) ? data.reason : 'generic');
            return;
          default:
            fail('generic');
        }
      } catch (error) {
        if (currentRun !== run) return;
        console.error("Error starting the streaming session:", error);
        fail('generic');
      }
    };

    poll();
  }

  // Multi-source watch: only restarts when one of the ids actually changes
  watch([() => toValue(participationId), () => toValue(situationId)], start, { immediate: true });
  onBeforeUnmount(stop);

  return { url, status, errorKind, canRetry, retry: start };
}
